import axios from 'axios';
import { isNil } from 'lodash';
import React from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import env from 'utils/env';
import { setItem, removeItem, getSiteCode } from 'utils/local-storage';
import { ACCESS_TOKEN, DISCOUNT_CODE, SESSION_KEY, SESSION_KEY_EXPIRY, URL_QUERY_STRING, USER_PROFILE } from 'utils/storage-keys';

declare const window : any

export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return null;
};

export const getSessionTokenFromCookie = (): string | null => {
  const authData = getCookie('auth_data');
    if (!authData) {
      return null;
    }

    try {
      const authDataJson = JSON.parse(atob(authData.replaceAll('"', '')));
      return authDataJson?.access_token || null;
    } catch(error) {
      return null;
    }
};

export const getUserProfileFromCookie = (): string | null => {
  const authData = getCookie('auth_data');
    if (!authData) {
      return null;
    }

    try {
      return JSON.parse(atob(authData.replaceAll('"', '')));
    } catch(error) {
      return null;
    }
};

export const loadSession = () => {
  let sessionToken = localStorage.getItem(SESSION_KEY);
  if (isNil(sessionToken)) {
    sessionToken = getSessionTokenFromCookie();
    const userProfile = getUserProfileFromCookie();
    if (isNil(sessionToken)) {
      removeItem(SESSION_KEY);
      removeItem(SESSION_KEY_EXPIRY);
      removeItem(ACCESS_TOKEN);
      removeItem(USER_PROFILE);
      return null;
    }
    setItem(ACCESS_TOKEN, sessionToken);
    setItem(SESSION_KEY, sessionToken);
    setItem(USER_PROFILE, JSON.stringify(userProfile));
    setItem(SESSION_KEY_EXPIRY, moment().add(1, 'days').toDate().getTime().toString());

    return sessionToken;
  }

  const sessionTokenExpiry = localStorage.getItem(SESSION_KEY_EXPIRY);
  if (isNil(sessionTokenExpiry)) {
    removeItem(SESSION_KEY);
    removeItem(SESSION_KEY_EXPIRY);
    removeItem(ACCESS_TOKEN);
    removeItem(USER_PROFILE);
    return null;
  }

  const expiredDate = Number.parseInt(sessionTokenExpiry.toString(), 10);
  if (expiredDate <= Date.now()) {
    removeItem(SESSION_KEY);
    removeItem(SESSION_KEY_EXPIRY);
    removeItem(ACCESS_TOKEN);
    removeItem(USER_PROFILE);
    return null;
  }

  return sessionToken;
};

const useSession = () => {
  const [session, setSession] = React.useState<string | null>(null);
  const [bootstrap, setBootstrap] = React.useState<boolean>(true);
  const navigate = useNavigate();
  const siteCode = getSiteCode();

  React.useEffect(() => {
    const sessionToken = loadSession();
    setBootstrap(false);
    setSession(sessionToken);
  }, []);

  React.useEffect(()=> {
    async function getDiscountCode() {
      const config = env();
      const { data } = await axios.get(`${config.userServerApiUrl}/product-catalog/v1/site/${siteCode}/paywall?country=ID&language=id`);
      if (data?.name) {
        setItem(DISCOUNT_CODE, data.name);
      } else {
        setItem(DISCOUNT_CODE, 'IDTEL');
      }
    }
    getDiscountCode();
  }, [siteCode]);

  const storeSession = React.useCallback(
    (sessionToken: string, expiry: Date) => {
      setItem(SESSION_KEY, sessionToken);
      setItem(SESSION_KEY_EXPIRY, expiry.getTime().toString());

      setSession(sessionToken);
    },
    []
  );

  const signOut = () => {
    removeItem(SESSION_KEY);
    removeItem(ACCESS_TOKEN);
    removeItem(URL_QUERY_STRING);
    removeItem(USER_PROFILE);
    window.location.href = `${env().universalLoginUrl}/logout?redirectUrl=${window.location.href}`;
  };

  return {
    session,
    bootstrap,
    storeSession,
    signOut
  };
};

export default useSession;
